import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, RendererFactory2 } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { JitsiInstance } from '../jitsiInstance';

declare const JitsiMeetExternalAPI: any;
type buttonsConfigKeys = 'microphone' | 'camera' | 'closedcaptions' | 'desktop' | 'embedmeeting' | 'fullscreen' |
  'fodeviceselection' | 'hangup' | 'profile' | 'chat' | 'recording' |
  'livestreaming' | 'etherpad' | 'sharedvideo' | 'settings' | 'raisehand' |
  'videoquality' | 'filmstrip' | 'invite' | 'feedback' | 'stats' | 'shortcuts' |
  'tileview' | 'select-background' | 'download' | 'help' | 'mute-everyone' | 'mute-video-everyone' |
  'security';

@Injectable({providedIn: 'root'})
export class JitsiAPIService {

  private _api: Observable<void> | undefined;

  constructor(private _rendererFactory: RendererFactory2, @Inject(DOCUMENT) private _document: Document) {
  }


  connectToRoom(containerElement: HTMLElement,
                options: {
                  roomName: string | undefined;
                  jwt?: string;
                  applyCustomConfigs?: boolean;
                  buttonsConfig?: { [key in buttonsConfigKeys]?: boolean };
                  customConfigs?: any;
                  onload?: () => void;
                  userInfo?: {
                    displayName: string | undefined;
                    email?: string;
                  }
                },
                domain = 'meet.jit.si'): Observable<JitsiInstance> {
    const toolBarButtons: Array<buttonsConfigKeys> = [
      'microphone', 'camera', 'closedcaptions', 'desktop', /*'embedmeeting', */ 'fullscreen',
      'fodeviceselection', 'hangup', 'profile', 'chat', /* 'recording',
        'livestreaming',*/ 'etherpad', /*'sharedvideo',*/ 'settings', 'raisehand',
      'videoquality', 'filmstrip' /*'invite'*/, 'feedback', 'stats', 'shortcuts',
      'tileview', 'select-background', 'download', 'help', 'mute-everyone', /*'mute-video-everyone',*/
      /*'security'*/
    ];
    if (options.buttonsConfig) {
      for (const [key, value] of Object.entries(options.buttonsConfig)) {
        const buttonKey = key as buttonsConfigKeys;
        if (value) {
          if (toolBarButtons.indexOf(buttonKey) === -1) {
            toolBarButtons.push(buttonKey);
          }
        } else {
          const keyIndex = toolBarButtons.indexOf(buttonKey);
          if (keyIndex !== -1) {
            toolBarButtons.splice(keyIndex, 1);
          }
        }
      }
    }
    return this.init(domain).pipe(
      map(() => {
        const apiOptions = {
          ...options,
          parentNode: containerElement,
          ...(options.applyCustomConfigs ? {
            // customize default config
            configOverwrite: {
              enableWelcomePage: false,
              enableCalendarIntegration: false,
              disableInviteFunctions: true,
              doNotStoreRoom: true,
              disableAudioLevels: true,
              prejoinPageEnabled: false,
              ...options.customConfigs
              // brandingDataUrl: 'https://myiaprod.blob.core.windows.net/streaming-resources/jitsi-branding/jitsi-myia-branding.json'
            },
            interfaceConfigOverwrite: {
              SHOW_JITSI_WATERMARK: false,
              VIDEO_QUALITY_LABEL_DISABLED: true, // remove HD icon beside participant
              SHOW_PROMOTIONAL_CLOSE_PAGE: false,
              SHOW_WATERMARK_FOR_GUESTS: false,
              SHOW_CHROME_EXTENSION_BANNER: false,
              TOOLBAR_BUTTONS: toolBarButtons,
              LANG_DETECTION: !(options.customConfigs?.defaultLanguage), // https://developer.8x8.com/jaas/docs/customize-ui-default-language
              DISABLE_FOCUS_INDICATOR: true,
              RECENT_LIST_ENABLED: false,
              DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
              // DEFAULT_LOGO_URL: 'https://myiaprod.blob.core.windows.net/streaming-resources/jitsi-branding/watermark.png'

            }
          } : {})
        };
        return new JitsiInstance(options.roomName, new JitsiMeetExternalAPI(domain, apiOptions));
      })
    );
  }


  private init(domain: string): Observable<void> {
    if (!this._api) {
      const renderer = this._rendererFactory.createRenderer(null, null);
      this._api = new Observable((observer: Observer<void>) => {
        const script = renderer.createElement('script');
        script.src = `https://${domain}/external_api.js`;
        script.onload = () => {
          console.log('Jitsi API script loaded');
          observer.next();
          observer.complete();
        };
        renderer.appendChild(this._document.body, script);
      }).pipe(
        // cache -> return same result for all subscriptions
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
    }
    return this._api;
  }
}
